<template>
  <div class="redirect-page">
    <LoadingContent :text="t('redirect.loading')" />
    <AppHooks />
  </div>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import { z } from 'zod'

import AppHooks from '@/(routes)/(protected)/(app)/app-hooks.vue'
import { consumeReturnTo } from '@/features/auth/hooks/use-return-to.hook'
import { selectGetUserActivePermissionGroup } from '@/features/auth/stores/auth/auth.selectors'
import authStore from '@/features/auth/stores/auth/auth.store'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import { PermissionsGroupsEnumType } from '@/features/auth/types'
import programsStore from '@/features/programs/stores/programs/programs.store'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import useRouter from '@/hooks/use-router.hook'
import { useI18n } from '@/lib/i18n'
import { ROUTES } from '@/utils/config/constants'

const { t } = useI18n()
const { programRolesRef } = useProgramRoles()
const router = useRouter()
const userActivePermissionGroupRef = selectGetUserActivePermissionGroup(authStore)

// Restitue (une seule fois) le deep-link capturé avant le mur d'auth.
// Le token a déjà été retiré côté composable (anti-fuite), de même que `from`.
const returnTo = consumeReturnTo()

// Query string à propager vers la page cible. Priorité au deep-link restitué,
// sinon la query courante de /redirect, dans les deux cas sans `token`.
const deepLinkQuery = ((): Record<string, string> => {
  const source = returnTo ?? `${router?.currentRoute.fullPath ?? ''}`
  try {
    const params = new URL(source, window.location.origin).searchParams
    params.delete('token')
    params.delete('from')
    return Object.fromEntries(params.entries())
  } catch {
    return {}
  }
})()

// Sélectionne le programme du deep-link s'il est valide (UUID), pour que la
// page cible affiche le bon programme y compris après un round-trip SSO où la
// query d'origine n'était plus dans l'URL au boot de l'app.
const deepLinkProgramId = z.string().uuid().safeParse(deepLinkQuery.programId).data
if (deepLinkProgramId) {
  programsStore.setSelectedProgramId(deepLinkProgramId)
}

const pushWithQuery = (path: string) => {
  router?.push({ path, query: deepLinkQuery }).catch(() => {})
}

// Si le deep-link visait une route applicative précise (ex: /sessions?programId=),
// on la restitue directement plutôt que de retomber sur le dashboard par rôle.
const returnToPathname = returnTo ? new URL(returnTo, window.location.origin).pathname : null
const isSpecificAppTarget = returnToPathname && returnToPathname !== '/' && returnToPathname !== ROUTES.REDIRECT

if (isSpecificAppTarget) {
  router?.replace(returnTo!).catch(() => {})
}

// before mount, check if the user has admin or manager role and redirect to the admin or user dashboard
// the admin and manager have priority over the user for the permission group and displayed interface when logging in
watch(
  [userActivePermissionGroupRef, programRolesRef],
  ([userActivePermissionGroup, programRoles]) => {
    // une cible applicative précise a déjà été restituée : on ne touche plus à la navigation
    if (isSpecificAppTarget) return

    // if the user already has an active permission group, priority takes over the program roles
    if (userActivePermissionGroup === PermissionsGroupsEnumType.ADMIN) {
      pushWithQuery(ROUTES.ADMIN.DASHBOARD)
    } else if (userActivePermissionGroup === PermissionsGroupsEnumType.USER) {
      pushWithQuery(ROUTES.DASHBOARD)
    }

    // else, check if the user has admin or manager role and redirect to the admin or user dashboard
    if (programRoles?.length) {
      if (programRoles.includes(RoleEnumTypeV2.ADMIN) || programRoles.includes(RoleEnumTypeV2.MANAGER)) {
        pushWithQuery(ROUTES.ADMIN.DASHBOARD)
      } else {
        pushWithQuery(ROUTES.DASHBOARD)
      }
    }
  },
  { immediate: true },
)
</script>

<style lang="scss">
@import '@/features/ui/components/redirect/redirect.scss';
</style>
